/****************************************************************************
 * Copyright (C) 2017 ecsec GmbH.
 * All rights reserved.
 * Contact: ecsec GmbH (info@ecsec.de)
 *
 * This file is part of SkIDentity.
 *
 * This file may be used in accordance with the terms and conditions
 * contained in a signed written agreement between you and ecsec GmbH.
 *
 ***************************************************************************/

import sjcl = require("@ecsec/sjcl");
import { ExportedECKey, ECKey } from "./jwk";


const CURVE = sjcl.ecc.curves.c256;
const NONCE_SIZE = 256 / 32;
const SALT_SIZE = 256 / 32;

const DISPLAY_NUM_CHARS = 5;
const DISPLAY_BITS_PER_CHAR = 5;
//const DISPLAY_ALPHABET = [
//	"0", "1", "2", "3", "4", "5", "6", "7", "8", "9",
//	"C", "F", "G", "H", "J", "K", "L", "M", "N", "P", "R", "T", "V", "W", "X", "Y", "Z"
//];
const DISPLAY_ALPHABET = [
	"0", "1", "2", "3", "4", "5", "6", "7", "8", "9",
	"C", "F", "G", "H", "J", "K", "L", "M", "N", "P",
	"R", "T", "V", "W", "X", "Y", "Z", "=", "%", "&",
	"+", "#"
];

///
/// Network messages
///

export interface IdentificationA {
	typ: "Trust";
	pk: ExportedECKey;
	nonce: string;
	description: string;
}

export interface IdentificationB {
	typ: "Trust";
	pk: ExportedECKey;
	nonce: string;
	cmt: string;
	description: string;
}

export interface LinkConfirmation {
	typ: "Link";
	cnf: string;
}

///
/// local messages
///

export interface PersistedParameter {
	prk_self: sjcl.SjclECCSecretKeySerialized;
	pk_other: sjcl.SjclECCPublicKeySerialized;
	nonce_self: number[];
	nonce_other: number[];
	peerNameOther: string;
	salt: number[];
	isWrapped: boolean;
}


export class KeyExchangeError extends Error {

}

export class PasswordMissingError extends Error {

}


export abstract class NumericKeyExchangeBase {

	protected keyPair: sjcl.SjclKeyPair<sjcl.SjclElGamalPublicKey, sjcl.SjclElGamalSecretKey>;
	protected nonceSelf: number[];
	protected pubKeyOther: sjcl.SjclECCPublicKey;
	protected nonceOther: number[];
	protected peerNameOther: string;
	protected salt: number[];

	public constructor(protected peerNameSelf: string) {

	}

	public init(keyPair?: sjcl.SjclKeyPair<sjcl.SjclElGamalPublicKey, sjcl.SjclElGamalSecretKey>,
		 nonceSelf?: number[],
		 salt?: number[]) {
		// generate values if they are not given
		this.keyPair = keyPair || sjcl.ecc.elGamal.generateKeys(CURVE);
		this.nonceSelf = nonceSelf || sjcl.random.randomWords(NONCE_SIZE);
		this.salt = salt || sjcl.random.randomWords(SALT_SIZE);
	}

	protected calculateNumericComparison(bits: number[]) : string {
		const len = sjcl.bitArray.bitLength(bits);
		const sumBits = DISPLAY_BITS_PER_CHAR * DISPLAY_NUM_CHARS;
		if (len < sumBits) {
			throw new KeyExchangeError("Not enough bits available in calculated comparison value.");
		}

		let result = "";
		for (let i = 1; i <= DISPLAY_NUM_CHARS; i++) {
			let start = len - (i * DISPLAY_BITS_PER_CHAR) - 1;
			let idx = sjcl.bitArray.extract(bits, start, DISPLAY_BITS_PER_CHAR);
			result += DISPLAY_ALPHABET[idx];
		}

		return result;
	}

	public exportParameters() : PersistedParameter {
		return {
			prk_self: this.keyPair.sec.serialize(),
			pk_other: this.pubKeyOther.serialize(),
			nonce_self: this.nonceSelf,
			nonce_other: this.nonceOther,
			peerNameOther: this.peerNameOther,
			salt: this.salt,
			isWrapped: false
		};
	}

}


export class NumericKeyExchangeInitiator extends NumericKeyExchangeBase {

	private commitmentB: number[];

	public provideIdentification(): IdentificationA {
		return {
			typ: "Trust",
			pk: new ECKey().loadSjclPubKey(this.keyPair.pub).exportJWK(),
			nonce: sjcl.codec.base64url.fromBits(this.nonceSelf),
			description: this.peerNameSelf
		};
	}

	public receiveIdentification(idB: IdentificationB) {
		this.nonceOther = sjcl.codec.base64url.toBits(idB.nonce);
		let exportKey = new ECKey().loadJWK(idB.pk).exportSjcl("elGamal");
		this.pubKeyOther = sjcl.ecc.deserialize(exportKey);
		this.peerNameOther = idB.description;
		this.commitmentB = sjcl.codec.base64url.toBits(idB.cmt);

		// calculate commitment value for ourselves
		let cbMac = new sjcl.misc.hmac(this.nonceOther, sjcl.hash.sha256);
		cbMac.update(this.pubKeyOther.get().x);
		cbMac.update(this.keyPair.pub.get().x);
		let cbCalc = cbMac.digest();

		// compare commitment value
		if (! sjcl.bitArray.equal(cbCalc, this.commitmentB)) {
			throw new KeyExchangeError("Invalid commitment value received from non initiating peer.");
		}
	}

	public getVisualConfirmation() : string {
		let hash = new sjcl.hash.sha256();
		hash.update(this.keyPair.pub.get().x);
		hash.update(this.pubKeyOther.get().x);
		hash.update(this.nonceSelf);
		hash.update(this.nonceOther);
		return this.calculateNumericComparison(hash.finalize());
	}

}

export class NumericKeyExchangeInitiatee extends NumericKeyExchangeBase {

	public receiveIdentification(idB: IdentificationA) : IdentificationB {
		this.nonceOther = sjcl.codec.base64url.toBits(idB.nonce);
		let exportKey = new ECKey().loadJWK(idB.pk).exportSjcl("elGamal");
		this.pubKeyOther = sjcl.ecc.deserialize(exportKey);
		this.peerNameOther = idB.description;

		// calculate commitment value
		let cbMac = new sjcl.misc.hmac(this.nonceSelf, sjcl.hash.sha256);
		cbMac.update(this.keyPair.pub.get().x);
		cbMac.update(this.pubKeyOther.get().x);
		let cbCalc = cbMac.digest();

		return {
			typ: "Trust",
			pk: new ECKey().loadSjclPubKey(this.keyPair.pub).exportJWK(),
			nonce: sjcl.codec.base64url.fromBits(this.nonceSelf),
			description: this.peerNameSelf,
			cmt: sjcl.codec.base64url.fromBits(cbCalc)
		};
	}

	public getVisualConfirmation() : string {
		let hash = new sjcl.hash.sha256();
		hash.update(this.pubKeyOther.get().x);
		hash.update(this.keyPair.pub.get().x);
		hash.update(this.nonceOther);
		hash.update(this.nonceSelf);
		return this.calculateNumericComparison(hash.finalize());
	}

}



export class LinkEstablishment {

	private secKeySelf: sjcl.SjclElGamalSecretKey;
	private pubKeyOther: sjcl.SjclECCPublicKey;
	private nonceSelf: number[];
	private nonceOther: number[];
	private peerNameOther: string;

	private salt: number[];
	private dhSecret: number[];

	public constructor(
		private initiator: boolean,
		private addrSelf: string,
		private addrOther: string,
		private versionSelf: string,
		private versionOther: string,
		private session: string
	) {

	}

	private init() {
		this.dhSecret = this.secKeySelf.dhJavaEc(this.pubKeyOther);
	}

	public exportParameters(pass?: string) : PersistedParameter {
		let prk = pass ? this.secKeySelf.mask(pass, this.salt) : this.secKeySelf;
		let wrapped = !!pass;

		return {
			prk_self: prk.serialize(),
			pk_other: this.pubKeyOther.serialize(),
			nonce_self: this.nonceSelf,
			nonce_other: this.nonceOther,
			peerNameOther: this.peerNameOther,
			salt: this.salt,
			isWrapped: wrapped
		};
	}

	public loadParameters(params: PersistedParameter, pass?: string) {
		this.pubKeyOther = sjcl.ecc.deserialize(params.pk_other) as sjcl.SjclElGamalPublicKey;
		this.nonceSelf = params.nonce_self;
		this.nonceOther = params.nonce_other;
		this.peerNameOther = params.peerNameOther;
		this.salt = params.salt;

		this.secKeySelf = sjcl.ecc.deserialize(params.prk_self) as sjcl.SjclElGamalSecretKey;
		if (params.isWrapped) {
			if (! pass) {
				throw new PasswordMissingError("No password is given for the wrapped DH key.");
			} else {
				this.secKeySelf.unmask(pass, this.salt);
			}
		}

		this.init();
	}


	public getOwnConfirmationValue(): LinkConfirmation {
		let cbMac = new sjcl.misc.hmac(this.dhSecret, sjcl.hash.sha256);
		cbMac.update(this.nonceSelf);
		cbMac.update(this.nonceOther);
		cbMac.update(this.addrSelf);
		cbMac.update(this.addrOther);
		cbMac.update(this.versionSelf);
		cbMac.update(this.versionOther);
		cbMac.update(this.session);
		let cbCalc = cbMac.digest();
		return {
			typ: "Link",
			cnf: sjcl.codec.base64url.fromBits(cbCalc)
		};
	}

	public isOtherConfirmationValid(cfOther: LinkConfirmation) {
		return cfOther.cnf === this.getOtherConfirmationValue();
	}

	private getOtherConfirmationValue() : string {
		let cbMac = new sjcl.misc.hmac(this.dhSecret, sjcl.hash.sha256);
		cbMac.update(this.nonceOther);
		cbMac.update(this.nonceSelf);
		cbMac.update(this.addrOther);
		cbMac.update(this.addrSelf);
		cbMac.update(this.versionOther);
		cbMac.update(this.versionSelf);
		cbMac.update(this.session);
		let cbCalc = cbMac.digest();
		return sjcl.codec.base64url.fromBits(cbCalc);
	}

	public getLinkKey() : number[] {
		let na, nb, aa, ab, va, vb;
		if (this.initiator) {
			na = this.nonceSelf;
			nb = this.nonceOther;
			aa = this.addrSelf;
			ab = this.addrOther;
			va = this.versionSelf;
			vb = this.versionOther;
		} else {
			nb = this.nonceSelf;
			na = this.nonceOther;
			ab = this.addrSelf;
			aa = this.addrOther;
			vb = this.versionSelf;
			va = this.versionOther;
		}

		let cbMac = new sjcl.misc.hmac(this.dhSecret, sjcl.hash.sha256);
		cbMac.update(na);
		cbMac.update(nb);
		cbMac.update("SkID");
		cbMac.update(aa);
		cbMac.update(ab);
		cbMac.update(va);
		cbMac.update(vb);
		let cbCalc = cbMac.digest();
		return cbCalc;
	}

}



export abstract class SecureChannelBase {

	protected counter = new sjcl.bn(1);
	protected cipher: sjcl.SjclCipher;

	public constructor(
		protected secret: number[],
		protected nonce: number[]
	) {
		this.cipher = new sjcl.cipher.aes(this.secret);
	}

	protected iv() : number[] {
		// left padded counter
		let s = [0, 0].concat(this.sToBits());
		let iv = sjcl.bitArray.bitSlice(this.nonce, 128);
		let result = sjcl.mode.cbc.encrypt(this.cipher, s, iv);
		return result;
	}

	protected sToBits() : sjcl.BitArray {
		return this.counter.toBits(64);
	}

	protected incrementCounter() {
		this.counter.addM(1);
	}

}

export class SecureChannelOut extends SecureChannelBase {

	public encrypt(msg: number[] | string) : number[] {
		msg = typeof msg === "string" ? sjcl.codec.utf8String.toBits(msg) : msg;
		let iv = this.iv();
		let cipherText = sjcl.mode.gcm.encrypt(this.cipher, msg, iv, this.sToBits());
		this.incrementCounter();
		return cipherText;
	}

	public encrypt_toB64(msg: number[] | string) : string {
		return sjcl.codec.base64url.fromBits(
			this.encrypt(msg)
		);
	}

}

export class SecureChannelIn extends SecureChannelBase {

	public decrypt(cipherText: number[]) : number[] {
		let iv = this.iv();
		let msg = sjcl.mode.gcm.decrypt(this.cipher, cipherText, iv, this.sToBits());
		this.incrementCounter();
		return msg;
	}

	public decryptText(cipherText: number[]) : string {
		let msgBytes = this.decrypt(cipherText);
		return sjcl.codec.utf8String.fromBits(msgBytes);
	}

	public decrypt_fromB64(cipherText: string) : string {
		let msgBytes = this.decrypt(sjcl.codec.base64url.toBits(cipherText));
		return sjcl.codec.utf8String.fromBits(msgBytes);
	}

}



// WEBPACK FOOTER //
// ./node_modules/@ecsec/base-libs/src/pairing/protocol.ts